import { crudPage } from '../ui/crud.js';
import { loader } from '../ui/loader.js';
import { supabase } from '../config/supabaseClient.js';
import { badgeStatus } from '../ui/badges.js';
import { el, clear, formatarNome, mascararCpf, mascararMatricula, fmtDataHora, soDigitos } from '../lib/dom.js';
import { buscarTodos } from '../lib/db.js';
import { renderTabela } from '../ui/table.js';
import { abrirModal } from '../ui/modal.js';
import { toast } from '../lib/toast.js';
import { ehGestaoAtivo } from '../lib/permissoes.js';
import { sessao } from '../lib/auth.js';
import { operadorAtual } from '../lib/operador.js';

const OPCOES_STATUS = [{ value: 'ATIVO', label: 'Ativo' }, { value: 'INATIVO', label: 'Inativo' }];
const MOTIVOS = [
  { value: 'JUDICIAL', label: 'Judicial' },
  { value: 'DISCIPLINAR', label: 'Disciplinar' },
  { value: 'ADMINISTRATIVO', label: 'Administrativo' },
];
const rotuloMotivo = (m) => (MOTIVOS.find((o) => o.value === m)?.label || m || '—');
const fmtData = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : 'Indefinido');

async function opcoesEmpregadores() {
  try {
    const data = await buscarTodos(() => supabase.from('empregador').select('id, empresa, proprio').eq('status', 'ATIVO').order('empresa'));
    return data.map((e) => ({ value: e.id, label: e.empresa, proprio: !!e.proprio }));
  } catch (e) {
    const data = await buscarTodos(() => supabase.from('empregador').select('id, empresa').eq('status', 'ATIVO').order('empresa'));
    return data.map((e) => ({ value: e.id, label: e.empresa, proprio: false }));
  }
}

// callback global p/ refazer as listas após mexer numa suspensão
let aoAtualizar = () => {};

/** Modal de Acesso suspenso (gestão): suspende, atualiza ou reativa. */
function abrirSuspensao(col) {
  const suspenso = !!col.acesso_suspenso;
  const nome = formatarNome(col.nome || '');
  const motivo = el('select', {}, [el('option', { value: '' }, '— selecione —'), ...MOTIVOS.map((o) => el('option', { value: o.value }, o.label))]);
  motivo.value = col.suspensao_motivo || '';
  const obs = el('textarea', { rows: '3', placeholder: 'Detalhes (opcional)' }); obs.value = col.suspensao_obs || '';
  const ate = el('input', { type: 'date' }); ate.value = col.suspensao_ate || '';

  const corpo = [
    el('p', { class: 'view__sub', style: 'margin-top:0', text: suspenso
      ? 'Este colaborador está com acesso suspenso como colaborador. Atualize os dados ou reative.'
      : 'Marcar acesso suspenso como colaborador. Enquanto suspenso, ele fica inativo (não aparece nas movimentações).' }),
    el('div', { class: 'campo' }, [el('label', { text: 'Motivo *' }), motivo]),
    el('div', { class: 'campo' }, [el('label', { text: 'Observação' }), obs]),
    el('div', { class: 'campo' }, [el('label', { text: 'Suspenso até (vazio = por tempo indeterminado)' }), ate]),
  ];

  async function salvar() {
    if (!motivo.value) { toast('Selecione o motivo.', 'erro'); return false; }
    const patch = {
      acesso_suspenso: true, suspensao_motivo: motivo.value,
      suspensao_obs: (obs.value || '').trim() || null,
      suspensao_ate: ate.value || null, status: 'INATIVO',
    };
    if (!suspenso) { patch.suspensao_em = new Date().toISOString(); patch.suspensao_por = sessao.usuario?.id || null; }
    const { error } = await supabase.from('colaborador').update(patch).eq('id', col.id);
    if (error) { toast('Erro: ' + error.message, 'erro'); return false; }
    toast(suspenso ? 'Suspensão atualizada.' : 'Acesso suspenso.', 'sucesso'); aoAtualizar(); return true;
  }
  async function reativar() {
    const { error } = await supabase.from('colaborador').update({
      acesso_suspenso: false, suspensao_motivo: null, suspensao_obs: null,
      suspensao_ate: null, suspensao_em: null, suspensao_por: null, status: 'ATIVO',
    }).eq('id', col.id);
    if (error) { toast('Erro: ' + error.message, 'erro'); return false; }
    toast('Acesso reativado.', 'sucesso'); aoAtualizar(); return true;
  }

  const acoes = [{ rotulo: 'Cancelar', classe: 'btn' }];
  if (suspenso) acoes.push({ rotulo: 'Reativar acesso', classe: 'btn', onClick: reativar });
  acoes.push({ rotulo: suspenso ? 'Salvar' : 'Suspender acesso', classe: 'btn btn--primario', onClick: salvar });
  abrirModal({ titulo: `Acesso suspenso — ${nome}`, corpo, acoes });
}

/** Marca quais linhas estão com acesso suspenso (carga à parte; tolerante se a 0027 não rodou). */
async function marcarSuspensos(linhas) {
  const { data, error } = await supabase.from('colaborador')
    .select('id, acesso_suspenso, suspensao_motivo, suspensao_obs, suspensao_ate')
    .eq('acesso_suspenso', true);
  if (error) return;
  const mapa = new Map((data || []).map((r) => [r.id, r]));
  linhas.forEach((l) => {
    const s = mapa.get(l.id);
    l.acesso_suspenso = !!s;
    if (s) { l.suspensao_motivo = s.suspensao_motivo; l.suspensao_obs = s.suspensao_obs; l.suspensao_ate = s.suspensao_ate; }
  });
}

const crud = crudPage({
  titulo: 'Colaboradores', singular: 'colaborador', semCabecalho: true,
  subtitulo: 'Pessoas que retiram e devolvem chaves.',
  tabela: 'colaborador',
  select: 'id, nome, cpf, matricula, status, empregador_id, empregador:empregador_id(empresa)',
  enriquecer: marcarSuspensos,
  ordenarPor: 'nome', asc: true,
  acoesLinha: (l) => ehGestaoAtivo()
    ? [el('button', { class: 'btn', title: 'Acesso suspenso', 'aria-label': 'Acesso suspenso', onClick: () => abrirSuspensao(l) }, [el('i', { class: `ph ph-lock-key${l.acesso_suspenso ? ' txt-alerta' : ''}` })])]
    : [],
  colunas: [
    { chave: 'nome', rotulo: 'Nome', render: (l) => formatarNome(l.nome) },
    { chave: 'cpf', rotulo: 'CPF', render: (l) => mascararCpf(l.cpf) },
    { chave: 'matricula', rotulo: 'Matrícula', render: (l) => mascararMatricula(l.matricula) },
    { chave: 'empregador', rotulo: 'Empregador', ordenavel: false, render: (l) => l.empregador?.empresa ?? '' },
    { chave: 'status', rotulo: 'Status', render: (l) => l.acesso_suspenso ? el('span', { class: 'badge badge--alerta', text: 'Acesso suspenso' }) : badgeStatus(l.status) },
  ],
  buscarEm: ['nome', 'cpf', 'matricula'],
  campos: [
    { chave: 'nome', rotulo: 'Nome', obrigatorio: true, cheio: true },
    { chave: 'empregador_id', rotulo: 'Empregador', tipo: 'busca', opcoes: opcoesEmpregadores, obrigatorio: true, cheio: true,
      aoSelecionar: (opcao, c) => { const terc = opcao ? !opcao.proprio : false; c.matricula.disabled = terc; c.matricula.placeholder = terc ? 'Preenchido com o CPF (terceirizado)' : ''; if (terc) c.matricula.value = soDigitos(c.cpf.value); } },
    { chave: 'cpf', rotulo: 'CPF', obrigatorio: true, mascara: 'cpf', meio: true,
      aoDigitar: (c) => { if (c.matricula.disabled) c.matricula.value = soDigitos(c.cpf.value); } },
    { chave: 'matricula', rotulo: 'Matrícula', obrigatorio: true, meio: true },
    { chave: 'status', rotulo: 'Status', tipo: 'select', opcoes: OPCOES_STATUS, padrao: 'ATIVO', cheio: true },
  ],
});

async function listarPendentes() {
  const { data, error } = await supabase.rpc('avulsos_pendentes');
  if (error) { console.error('[avulsos_pendentes]', error.message); return []; }
  return (data || []).map((r) => ({ nome: r.nome, vezes: r.vezes, ultima: r.ultima, motivo: r.ultimo_motivo || '', observacao: r.ultima_observacao || '', sugerido_por: r.sugerido_por || '' }));
}

/** Gestao "da baixa" num avulso que nao da para cadastrar: ele some da lista de pendentes.
 *  Reversivel: se a mesma pessoa voltar a pegar chave depois, reaparece. Requer migration 0069. */
function darBaixaAvulso(l, aoFeito) {
  const obs = el('textarea', { rows: '3', placeholder: 'Ex.: visitante de uma vez só / nome ilegível / não é colaborador…' });
  abrirModal({
    titulo: 'Dar baixa no avulso',
    corpo: [
      el('p', { class: 'view__sub', style: 'margin-top:0', text: `Arquivar "${formatarNome(l.nome)}"? Ele sai da lista de pendentes (use quando não dá para cadastrar). Se a mesma pessoa pegar chave de novo depois, volta a aparecer.` }),
      el('div', { class: 'campo' }, [el('label', { text: 'Motivo (opcional)' }), obs]),
    ],
    acoes: [
      { rotulo: 'Cancelar', classe: 'btn' },
      { rotulo: 'Dar baixa', classe: 'btn btn--perigo', onClick: async () => {
        const chave = (l.nome || '').trim().toLowerCase();
        if (!chave) { toast('Nome inválido.', 'erro'); return false; }
        const { error } = await supabase.from('avulso_baixa').upsert({
          portador_chave: chave, portador_nome: l.nome,
          motivo: (obs.value || '').trim() || null,
          baixado_por: sessao.usuario?.id || null, baixado_nome: operadorAtual?.nome || null,
          baixado_em: new Date().toISOString(),
        }, { onConflict: 'portador_chave' });
        if (error) {
          const code = error.code || '';
          const ausente = code === '42P01' || code === 'PGRST205' || code === 'PGRST204' || /could not find the table|schema cache/i.test(error.message || '');
          const semPermissao = code === '42501' || /row-level security|violates row-level/i.test(error.message || '');
          toast(ausente ? 'Recurso ainda não disponível: rode a migração 0069 no banco.'
            : semPermissao ? 'Sem permissão para dar baixa — faça por uma sessão de gestor.'
            : 'Não foi possível dar baixa: ' + error.message, 'erro');
          return false;
        }
        toast('Avulso arquivado.', 'info'); aoFeito?.(); return true;
      } },
    ],
  });
}

async function listarSuspensos() {
  try {
    return await buscarTodos(() => supabase.from('colaborador')
      .select('id, nome, cpf, matricula, acesso_suspenso, suspensao_motivo, suspensao_obs, suspensao_ate, suspensao_em')
      .eq('acesso_suspenso', true).order('nome'));
  } catch { return []; }
}

export const paginaColaboradores = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Colaboradores' }),
      el('p', { class: 'view__sub', text: 'Pessoas que retiram e devolvem chaves.' }),
    );

    const badgePend = el('span', { class: 'aba__contador', text: '…' });
    const badgeSusp = el('span', { class: 'aba__contador', text: '…' });
    const btnCad = el('button', { class: 'aba aba--ativa', onClick: () => trocar('cad') }, 'Cadastrados');
    const btnPend = el('button', { class: 'aba', onClick: () => trocar('pend') }, [el('span', { text: 'Pendentes (avulsos) ' }), badgePend]);
    const btnSusp = el('button', { class: 'aba', onClick: () => trocar('susp') }, [el('span', { text: 'Acesso suspenso ' }), badgeSusp]);
    const divCad = el('div');
    const divPend = el('div', { style: 'display:none' });
    const divSusp = el('div', { style: 'display:none' });
    container.append(el('div', { class: 'abas' }, [btnCad, btnPend, btnSusp]), divCad, divPend, divSusp);

    crud.render(divCad);

    let pendFeito = false, suspFeito = false;
    function trocar(a) {
      btnCad.classList.toggle('aba--ativa', a === 'cad');
      btnPend.classList.toggle('aba--ativa', a === 'pend');
      btnSusp.classList.toggle('aba--ativa', a === 'susp');
      divCad.style.display = a === 'cad' ? '' : 'none';
      divPend.style.display = a === 'pend' ? '' : 'none';
      divSusp.style.display = a === 'susp' ? '' : 'none';
      if (a === 'pend' && !pendFeito) { pendFeito = true; desenharPend(); }
      if (a === 'susp' && !suspFeito) { suspFeito = true; desenharSusp(); }
    }

    aoAtualizar = () => { crud.recarregar?.(); desenharSusp(); atualizarBadges(); };

    async function desenharPend() {
      clear(divPend);
      const box = el('div');
      divPend.append(
        el('p', { class: 'view__sub', text: 'Pessoas que pegaram chave sem cadastro (avulso). Cadastre para parar de digitar o nome a cada vez.' }),
        box,
      );
      box.append(loader());
      const linhas = await listarPendentes();
      badgePend.textContent = String(linhas.length);
      clear(box);
      if (!linhas.length) { box.append(el('p', { class: 'view__sub', text: 'Nenhuma pessoa avulsa pendente.' })); return; }
      const gestao = ehGestaoAtivo();
      renderTabela(box, {
        colunas: [
          { chave: 'nome', rotulo: 'Nome', render: (l) => formatarNome(l.nome) },
          { chave: 'vezes', rotulo: 'Vezes', largura: '70px', alinhar: 'centro' },
          { chave: 'ultima', rotulo: 'Última vez', render: (l) => fmtDataHora(l.ultima) },
          { chave: 'motivo', rotulo: 'Último motivo', truncar: true },
          { chave: 'sugerido_por', rotulo: 'Sugerido por', render: (l) => formatarNome(l.sugerido_por || '') || '—' },
          { chave: 'observacao', rotulo: 'Observação', truncar: true },
          { chave: '_acao', rotulo: '', ordenavel: false,
            render: (l) => el('div', { style: 'display:flex;gap:6px;justify-content:flex-end' }, [
              el('button', { class: 'btn btn--primario', title: 'Cadastrar', 'aria-label': 'Cadastrar', onClick: () => crud.abrirNovo({ nome: l.nome }, () => desenharPend()) },
                [el('i', { class: 'ph ph-user-plus' })]),
              ...(gestao ? [el('button', { class: 'btn', title: 'Dar baixa (arquivar este avulso)', 'aria-label': 'Dar baixa', onClick: () => darBaixaAvulso(l, () => desenharPend()) }, [el('i', { class: 'ph ph-archive' })])] : []),
            ]) },
        ],
        linhas, buscarEm: ['nome', 'motivo', 'observacao', 'sugerido_por'], porPagina: 10,
      });
    }

    async function desenharSusp() {
      clear(divSusp);
      const box = el('div');
      divSusp.append(
        el('p', { class: 'view__sub', text: 'Colaboradores com acesso suspenso como colaborador (judicial, disciplinar ou administrativo). Eles ficam inativos enquanto durar a suspensão.' }),
        box,
      );
      box.append(loader());
      const linhas = await listarSuspensos();
      badgeSusp.textContent = String(linhas.length);
      clear(box);
      if (!linhas.length) { box.append(el('p', { class: 'view__sub', text: 'Nenhum colaborador com acesso suspenso.' })); return; }
      const colunas = [
        { chave: 'nome', rotulo: 'Nome', render: (l) => formatarNome(l.nome) },
        { chave: 'cpf', rotulo: 'CPF', ordenavel: false, render: (l) => mascararCpf(l.cpf) },
        { chave: 'matricula', rotulo: 'Matrícula', ordenavel: false, render: (l) => mascararMatricula(l.matricula) },
        { chave: 'suspensao_motivo', rotulo: 'Motivo', render: (l) => rotuloMotivo(l.suspensao_motivo) },
        { chave: 'suspensao_ate', rotulo: 'Até', render: (l) => fmtData(l.suspensao_ate) },
        { chave: 'suspensao_obs', rotulo: 'Observação', render: (l) => l.suspensao_obs || '' },
        { chave: 'suspensao_em', rotulo: 'Suspenso em', render: (l) => fmtDataHora(l.suspensao_em) },
      ];
      if (ehGestaoAtivo()) colunas.push({ chave: '_acao', rotulo: '', ordenavel: false,
        render: (l) => el('button', { class: 'btn', title: 'Editar / reativar', 'aria-label': 'Editar', onClick: () => abrirSuspensao(l) }, [el('i', { class: 'ph ph-lock-key' })]) });
      renderTabela(box, { colunas, linhas, buscarEm: ['nome', 'cpf', 'matricula', 'suspensao_obs'], porPagina: 12 });
    }

    function atualizarBadges() {
      listarPendentes().then((l) => { badgePend.textContent = String(l.length); });
      listarSuspensos().then((l) => { badgeSusp.textContent = String(l.length); });
    }

    atualizarBadges();
  },
};